import React from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import SEO from '../components/SEO';
import { CheckCircle, Package, MapPin, CreditCard, Home, ShoppingBag } from 'lucide-react';

const OrderSuccessPage = () => {
  const { orderId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { getCartCount } = useCart();
  
  const orderData = location.state?.orderData;
  const customerInfo = location.state?.customerInfo;

  if (!orderData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header cartCount={getCartCount()} />
        <div className="container mx-auto px-4 py-20 text-center">
          <p className="text-gray-600">Sipariş bilgisi bulunamadı.</p>
          <Link to="/" className="text-red-600 hover:underline mt-4 inline-block">
            Ana Sayfaya Dön
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Sipariş Başarılı - Cep Premium"
        description="Siparişiniz başarıyla alındı"
      />
      <Header cartCount={getCartCount()} />

      <div className="container mx-auto px-4 py-12">
        {/* Success Header */}
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
                <CheckCircle size={48} className="text-green-600" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Siparişiniz Alındı!
              </h1>
              <p className="text-gray-600 text-lg">
                Teşekkür ederiz. Siparişiniz başarıyla oluşturuldu.
              </p>
            </div>

            {/* Order Number */}
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 mb-6">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-1">Sipariş Numaranız</p>
                <p className="text-3xl font-bold text-red-600">#{orderId}</p>
                <p className="text-sm text-gray-600 mt-2">
                  Bu numarayı sipariş takibi için kullanabilirsiniz
                </p>
              </div>
            </div>

            {/* Order Details */}
            <div className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Sipariş Detayları</h2>
              
              <div className="space-y-4">
                {/* Customer Info */}
                <div className="flex items-start gap-3">
                  <MapPin size={20} className="text-gray-400 mt-1" />
                  <div>
                    <p className="font-medium text-gray-900">Teslimat Adresi</p>
                    <p className="text-gray-600 text-sm">
                      {customerInfo?.firstName} {customerInfo?.lastName}
                    </p>
                    <p className="text-gray-600 text-sm">{customerInfo?.address}</p>
                    <p className="text-gray-600 text-sm">
                      {customerInfo?.city} {customerInfo?.zipCode}
                    </p>
                    <p className="text-gray-600 text-sm">{customerInfo?.phone}</p>
                  </div>
                </div>

                {/* Payment Method */}
                <div className="flex items-start gap-3">
                  <CreditCard size={20} className="text-gray-400 mt-1" />
                  <div>
                    <p className="font-medium text-gray-900">Ödeme Yöntemi</p>
                    <p className="text-gray-600 text-sm">Kredi Kartı</p>
                  </div>
                </div>

                {/* Products */}
                <div className="flex items-start gap-3">
                  <Package size={20} className="text-gray-400 mt-1" />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 mb-2">Ürünler</p>
                    <div className="space-y-2">
                      {orderData.items?.map((item, index) => (
                        <div key={index} className="flex justify-between text-sm">
                          <span className="text-gray-600">
                            {item.name} x {item.quantity}
                          </span>
                          <span className="font-medium text-gray-900">
                            {(item.price * item.quantity).toFixed(2)} TL
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="border-t border-gray-200 mt-3 pt-3 flex justify-between">
                      <span className="font-semibold text-gray-900">Toplam</span>
                      <span className="font-bold text-red-600 text-lg">
                        {orderData.total?.toFixed(2)} TL
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Next Steps */}
            <div className="bg-blue-50 rounded-lg p-6 mt-6">
              <h3 className="font-semibold text-gray-900 mb-2">Sırada Ne Var?</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                  Siparişiniz onaylandıktan sonra email ile bilgilendirileceksiniz
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                  Kargoya verildiğinde takip numarası gönderilecektir
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                  Sipariş numaranız ile takip edebilirsiniz
                </li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <Link
                to="/siparis-takip"
                className="flex-1 flex items-center justify-center gap-2 bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors"
              >
                <Package size={20} />
                Siparişi Takip Et
              </Link>
              <Link
                to="/"
                className="flex-1 flex items-center justify-center gap-2 bg-white border-2 border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
              >
                <Home size={20} />
                Ana Sayfaya Dön
              </Link>
            </div>

            <div className="text-center mt-6">
              <Link
                to="/products"
                className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 font-medium"
              >
                <ShoppingBag size={18} />
                Alışverişe Devam Et
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default OrderSuccessPage;
